import React, { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutList, Mail, Settings2, Inbox, Users, Settings, FileCode, LogOut, Shield, Send, Clock, List, Tag, Database, Key, UserCircle, Zap, AlertTriangle, FolderOpen, MapPin, LayoutDashboard, Target, LayoutTemplateIcon, BookOpenIcon, GlobeIcon } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  section?: string;
}
const navigation: NavItem[] = [{
  name: 'Dashboard',
  href: '/dashboard',
  icon: LayoutDashboard
}, {
  name: 'Mailing Lists',
  href: '/mailing-lists',
  icon: Database,
  section: 'CONTENT'
}, {
  name: 'Newsletters',
  href: '/newsletters',
  icon: List,
  section: 'CONTENT'
}, {
  name: 'Contacts',
  href: '/contacts',
  icon: UserCircle,
  section: 'CONTENT'
}, {
  name: 'Signup Forms',
  href: '/forms',
  icon: LayoutList,
  section: 'FORMS & TEMPLATES'
}, {
  name: 'Form Templates',
  href: '/form-templates',
  icon: LayoutTemplateIcon,
  section: 'FORMS & TEMPLATES'
}, {
  name: 'Email Templates',
  href: '/templates',
  icon: Mail,
  section: 'FORMS & TEMPLATES'
}, {
  name: 'Email Library',
  href: '/email-templates-library',
  icon: BookOpenIcon,
  section: 'FORMS & TEMPLATES'
}, {
  name: 'Landing Pages',
  href: '/landing-pages',
  icon: GlobeIcon,
  section: 'FORMS & TEMPLATES'
}, {
  name: 'Form Configuration',
  href: '/configurations',
  icon: Settings2,
  section: 'FORMS & TEMPLATES'
}, {
  name: 'Campaigns',
  href: '/campaigns',
  icon: Send,
  section: 'CAMPAIGNS'
}, {
  name: 'Audiences',
  href: '/audiences',
  icon: Target,
  section: 'CAMPAIGNS'
}, {
  name: 'Send Buckets',
  href: '/send-buckets',
  icon: Database,
  section: 'CAMPAIGNS'
}, {
  name: 'Tags',
  href: '/tags',
  icon: Tag,
  section: 'ORGANIZATION'
}, {
  name: 'Categories',
  href: '/categories',
  icon: FolderOpen,
  section: 'ORGANIZATION'
}, {
  name: 'Regions',
  href: '/regions',
  icon: MapPin,
  section: 'ORGANIZATION'
}, {
  name: 'Submissions',
  href: '/submissions',
  icon: Inbox,
  section: 'DATA'
}, {
  name: 'Subscriptions',
  href: '/subscriptions',
  icon: Users,
  section: 'DATA'
}, {
  name: 'Audit Log',
  href: '/audit-log',
  icon: Clock,
  section: 'DATA'
}, {
  name: 'Team Users',
  href: '/users',
  icon: Users,
  section: 'ADMIN'
}, {
  name: 'Roles & Permissions',
  href: '/roles',
  icon: Key,
  section: 'ADMIN'
}, {
  name: 'Settings',
  href: '/settings',
  icon: Settings,
  section: 'ADMIN'
}, {
  name: 'API Test',
  href: '/api-test',
  icon: Zap,
  section: 'ADMIN'
}, {
  name: 'Error Log',
  href: '/error-log',
  icon: AlertTriangle,
  section: 'ADMIN'
}];
export function Sidebar() {
  const location = useLocation();
  const {
    currentUser,
    currentWorkspace
  } = useWorkspace();
  const primaryColor = currentWorkspace.primaryColor || '#2563eb';
  let lastSection: string | undefined = undefined;
  return <div className="flex flex-col w-64 flex-shrink-0 border-r border-gray-200 bg-white h-screen sticky top-0" data-id="element-982">
      {/* Logo - Fixed height */}
      <div className="flex h-16 items-center px-6 border-b border-gray-200 flex-shrink-0" data-id="element-983">
        <div className="flex items-center gap-2 font-semibold text-gray-900" data-id="element-984">
          <FileCode className="h-6 w-6 flex-shrink-0" style={{
          color: primaryColor
        }} data-id="element-985" />
          <span className="truncate" data-id="element-986">ConfigConsole</span>
        </div>
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto px-3 py-4" data-id="element-987">
        <div className="space-y-0.5" data-id="element-988">
          {navigation.map(item => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          if (item.href === '/roles' && currentUser.Role !== 'Super Admin') {
            return null;
          }
          // Render section label when section changes
          let sectionLabel = null;
          if (item.section && item.section !== lastSection) {
            sectionLabel = <div key={`section-${item.section}`} className={`px-3 pt-5 pb-1 ${lastSection !== undefined || !item.section ? '' : ''}`} data-id="element-989">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-990">
                    {item.section}
                  </span>
                </div>;
          }
          lastSection = item.section;
          return <Fragment key={item.name} data-id="element-991">
                {sectionLabel}
                <Link to={item.href} className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? 'text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} style={isActive ? {
              backgroundColor: 'var(--primary-lighter, #eff6ff)'
            } : undefined} data-id="element-992">
                  <item.icon className={`mr-3 h-5 w-5 flex-shrink-0 ${!isActive ? 'text-gray-400 group-hover:text-gray-500' : ''}`} style={isActive ? {
                color: primaryColor
              } : undefined} data-id="element-993" />
                  <span className="truncate" data-id="element-994">{item.name}</span>
                </Link>
              </Fragment>;
        })}

          {currentUser.Role === 'Super Admin' && <Link to="/super-admin" className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors mt-6 whitespace-nowrap ${location.pathname.startsWith('/super-admin') ? 'bg-purple-50 text-purple-900' : 'text-purple-600 hover:bg-purple-50 hover:text-purple-900'}`} data-id="element-995">
              <Shield className="mr-3 h-5 w-5 flex-shrink-0" data-id="element-996" />
              <span className="truncate" data-id="element-997">Super Admin</span>
            </Link>}
        </div>
      </nav>

      {/* User section - Fixed at bottom */}
      <div className="border-t border-gray-200 p-4 flex-shrink-0" data-id="element-998">
        <div className="flex items-center gap-3 px-3 py-2" data-id="element-999">
          <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0" style={{
          backgroundColor: primaryColor
        }} data-id="element-1000">
            {currentUser.User_Name?.charAt(0) || '?'}
          </div>
          <div className="flex-1 min-w-0" data-id="element-1001">
            <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1002">
              {currentUser.User_Name}
            </p>
            <p className="text-xs text-gray-500 truncate" data-id="element-1003">{currentUser.Role}</p>
          </div>
          <button onClick={() => {
          if (confirm('Sign out of ConfigConsole?')) {
            try {
              localStorage.removeItem('configconsole_user_email');
              window.location.reload();
            } catch {}
          }
        }} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Sign out" data-id="element-1004">
            <LogOut className="h-4 w-4" data-id="element-1005" />
          </button>
        </div>
      </div>
    </div>;
}